import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const InjectionList = ({ injections = [], onAddInjection, onEditInjection, onDeleteInjection }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Flatten nested data for table
    const flattenedInjections = injections.map((item) => ({
        id: item.injectionPatientInfo?.id || Math.random().toString(36).substr(2, 9), // fallback unique ID
        name: item.injectionPatientInfo?.name || "-",
        patient_type: item.injectionPatientInfo?.patient_type || "-",
        created_by: item.injectionPatientInfo?.creator?.name || "-",
        updated_by: item.injectionPatientInfo?.updater?.name || "-",
        on_duty_injections: item.injectionPatientInfo?.on_duty_injections || [],
        rn_injections: item.injectionPatientInfo?.rn_injections || [],
    }));

    // Filtering
    const filteredInjections = flattenedInjections.filter((inj) =>
        Object.values(inj).some((v) =>
            String(v || "").toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredInjections.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    // Custom Pagination
    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px", fontSize: "0.875rem" }}
                        value={rowsPerPage}
                        onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
                    >
                        {[5, 10, 15, 20, 25].map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    // Columns
    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "80px",
        },
        { name: "Client Name", selector: (row) => row.name, sortable: true },
        { name: "Patient Type", selector: (row) => row.patient_type, sortable: true },
        { name: "On Duty Doctor", selector: (row) => row.on_duty_injections?.[0]?.doctor_name || "-", sortable: true },
        { name: "RN / Dispenser", selector: (row) => row.rn_injections?.[0]?.rn_name || "-", sortable: true },
        { name: "Created By", selector: (row) => row.created_by, sortable: true },
        { name: "Updated By", selector: (row) => row.updated_by, sortable: true },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditInjection && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit Injection"
                            onClick={() => onEditInjection(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDeleteInjection && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Injection"
                            onClick={() => onDeleteInjection(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "150px",
        },
    ];

    // Export to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Injection Records", 14, 10);
        const tableColumn = ["S.No", "Client Name", "Patient Type", "On Duty Doctor", "RN / Dispenser", "Created By", "Updated By"];
        const tableRows = [];

        filteredInjections.forEach((inj, index) => {
            tableRows.push([
                index + 1,
                inj.name || "",
                inj.patient_type || "",
                inj.on_duty_injections?.[0]?.doctor_name || "",
                inj.rn_injections?.[0]?.rn_name || "",
                inj.created_by,
                inj.updated_by,
            ]);
        });

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("injection_records.pdf");
    };

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Left: Export + Search */}
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredInjections}
                                    filename="injections_export.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search injection..."
                        className="form-control form-control-sm"
                        style={{ width: "200px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                {/* Center Info */}
                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} injections
                </h6>

                {/* Right: Add Button */}
                {onAddInjection && (
                    <div>
                        <button className="btn btn-sm btn-primary" title="Add Injection" onClick={onAddInjection}>
                            <i className="ri-add-line me-1"></i> Add Injection
                        </button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredInjections}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={<div className="p-3 text-muted">No injections found</div>}
                />
            </div>
        </div>
    );
};

export default InjectionList;
